import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AuthContext } from "./AuthContext";
import { fetchOrgProfileStatus, isAdminRole, isOrgProfileEmpty } from "../../services/orgProfileService";

export const OrgProfileContext = createContext(null);

export default function OrgProfileProvider({ children }) {
  const { role, user, loading: authLoading } = useContext(AuthContext);
  const [status, setStatus] = useState({
    loading: true,
    hasOrganisation: true,
    profileComplete: false,
    profileEmpty: true,
    wizardSkipped: false,
    missingFields: [],
    org: {},
  });

  const load = useCallback(async (fresh) => {
    if (authLoading) return;
    if (!user) {
      setStatus((s) => ({ ...s, loading: false }));
      return;
    }
    setStatus((s) => ({ ...s, loading: true }));
    try {
      const data = await fetchOrgProfileStatus({ fresh });
      setStatus({
        loading: false,
        hasOrganisation: Boolean(data?.has_organisation),
        profileComplete: Boolean(data?.profile_complete),
        profileEmpty: isOrgProfileEmpty(data),
        wizardSkipped: Boolean(data?.wizard_skipped),
        missingFields: data?.profile_missing_fields || [],
        org: data?.org || {},
      });
    } catch {
      setStatus((s) => ({ ...s, loading: false, profileEmpty: false }));
    }
  }, [authLoading, user]);

  const refetch = useCallback(() => load(true), [load]);

  useEffect(() => {
    load(false);
  }, [load]);

  const isAdmin = isAdminRole(role);

  return (
    <OrgProfileContext.Provider
      value={{
        status,
        refetch,
        isAdmin,
        mustCompleteWizard:
          isAdmin &&
          status.hasOrganisation &&
          status.profileEmpty &&
          !status.wizardSkipped,
        canUseApp: status.hasOrganisation && (status.profileComplete || !isAdmin),
        noOrganisation: !status.hasOrganisation,
      }}
    >
      {children}
    </OrgProfileContext.Provider>
  );
}

export function useOrgProfile() {
  return useContext(OrgProfileContext);
}
